import { ref } from 'vue';
import type { FlexTokenOptions } from '@/lib/flex';

/** Parses "MM / YY" into the shape Microform's createToken expects; null if invalid or in the past. */
export function parseExpiry(value: string, now: Date = new Date()): FlexTokenOptions | null {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 4) {
    return null;
  }

  const month = digits.slice(0, 2);
  const year = '20' + digits.slice(2);
  const m = Number(month);
  const y = Number(year);

  if (m < 1 || m > 12) {
    return null;
  }

  if (y * 12 + m < now.getFullYear() * 12 + now.getMonth() + 1) {
    return null;
  }

  return { expirationMonth: month, expirationYear: year };
}

/** Formats digits as "MM / YY" while typing. */
export function formatExpiry(raw: string, isDeleting: boolean): string {
  let digits = raw.replace(/\D/g, '').slice(0, 4);

  // "4" -> "04" so single-digit months don't wait for a second key
  if (digits.length === 1 && digits > '1') {
    digits = '0' + digits;
  }

  // Deleting past the slash shouldn't re-add it
  return digits.length >= 3 || (digits.length === 2 && !isDeleting)
    ? digits.slice(0, 2) + ' / ' + digits.slice(2)
    : digits;
}

export function useExpiry() {
  const value = ref('');
  const isFocused = ref(false);
  const isInvalid = ref(false);

  function onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const inputType = (event as InputEvent).inputType ?? '';

    value.value = formatExpiry(input.value, inputType.startsWith('delete'));
    // Keep the DOM in sync even when the formatted value equals the previous one.
    input.value = value.value;
    isInvalid.value = false;
  }

  function onFocus(): void {
    isFocused.value = true;
  }

  function onBlur(): void {
    isFocused.value = false;
    if (value.value) {
      isInvalid.value = !parseExpiry(value.value);
    }
  }

  /** Returns the parsed expiry, marking the field invalid if it isn't. */
  function validate(): FlexTokenOptions | null {
    const expiry = parseExpiry(value.value);
    isInvalid.value = !expiry;

    return expiry;
  }

  return { value, isFocused, isInvalid, onInput, onFocus, onBlur, validate };
}
